import { Spacer } from "@hackthenorth/north";
import { TextInput } from "north.js";
import React from "react";
import styled from "styled-components";

import { FormFieldLabelRow, OptionalLabel } from "src/shared/components";
import { useEventContext } from "src/shared/contexts";
import SlackAccountLinkButton from "src/views/profile/components/SlackAccountLinkButton";

import { hasText } from "../constants";
import { fieldLabelStyles, subtitleTextStyles } from "../styles";
import { ProfileFormValues } from "../types";

export interface ContactFieldsProps {
  staging: ProfileFormValues;
  setStagingField: <K extends keyof ProfileFormValues>(
    field: K,
    value: ProfileFormValues[K]
  ) => void;
  /** When true, show the non-blocking empty-contact nudge. */
  showErrors: boolean;
}

const CONTACT_FIELDS: {
  key: keyof Pick<
    ProfileFormValues,
    "contact_linkedin" | "contact_discord" | "contact_x"
  >;
  label: string;
  placeholder: string;
}[] = [
  {
    key: "contact_linkedin",
    label: "LinkedIn",
    placeholder: "Paste LinkedIn",
  },
  {
    key: "contact_discord",
    label: "Discord",
    placeholder: "Enter Discord tag",
  },
  {
    key: "contact_x",
    label: "X / Twitter",
    placeholder: "Enter X username",
  },
];

const ContactFields: React.FC<ContactFieldsProps> = ({
  staging,
  setStagingField,
  showErrors,
}) => {
  const { hackerDirectory } = useEventContext();
  const slackConnectEnabled = hackerDirectory.slackLinkingEnabled;
  const showBlankWarning =
    showErrors && !CONTACT_FIELDS.some((field) => hasText(staging[field.key]));

  return (
    <>
      <Heading>How to reach you</Heading>

      <HelperText>
        These are the social links other hackers will use to contact you, as
        in-platform messaging is not supported. If you need to change any of
        your handles later, you can easily update them in your profile.
      </HelperText>
      <Spacer height={12} />
      <HelperText>
        {slackConnectEnabled ? (
          <>
            We highly encourage using Slack for team communication. Link your
            account so other hackers can start conversations with you.
          </>
        ) : (
          <>
            We highly encourage using Slack for team communication. Connection
            is disabled until the directory opens, so make sure to link your
            account then!
          </>
        )}
      </HelperText>
      <Spacer height={24} />

      <ContactGrid>
        <FieldCell>
          <FormFieldLabelRow>
            <FieldLabel>Slack</FieldLabel>
            <OptionalLabel>Optional</OptionalLabel>
          </FormFieldLabelRow>
          <Spacer height={8} />
          <SlackAccountLinkButton fullWidth size="md" />
        </FieldCell>

        {CONTACT_FIELDS.map((field) => (
          <FieldCell key={field.key}>
            <FormFieldLabelRow>
              <FieldLabel>{field.label}</FieldLabel>
              <OptionalLabel>Optional</OptionalLabel>
            </FormFieldLabelRow>
            <Spacer height={8} />
            <TextInputStyled
              placeholder={field.placeholder}
              value={staging[field.key] ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setStagingField(
                  field.key,
                  hasText(e.target.value) ? e.target.value : null
                )
              }
            />
          </FieldCell>
        ))}
      </ContactGrid>

      {showBlankWarning && (
        <>
          <Spacer height={12} />
          <BlankWarning>
            Add at least one contact method so other hackers can reach you.
          </BlankWarning>
        </>
      )}
    </>
  );
};

export default ContactFields;

const Heading = styled.h3`
  font-size: 20px;
  font-weight: 900;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  line-height: 1.5;
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  margin-bottom: 16px;
`;

const HelperText = styled.p`
  ${subtitleTextStyles}
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  margin: 0;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 24px;

  ${({ theme }) => theme.mediaQueries.tablet`
    grid-template-columns: 1fr;
  `}
`;

const FieldCell = styled.div`
  min-width: 0;
`;

const FieldLabel = styled.p`
  ${fieldLabelStyles}
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  margin: 0;
`;

const TextInputStyled = styled(TextInput).attrs({ size: "md" })`
  font-family: ${({ theme }) =>
    theme.globalConstants.fontFamily.body} !important;
  font-weight: 400 !important;
  font-size: 16px !important;
  line-height: 150% !important;
  letter-spacing: 0 !important;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) =>
      theme.globalConstants.color.textTertiary} !important;
  }
`;

const BlankWarning = styled.p`
  ${subtitleTextStyles}
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textWarning};
`;
